import app from 'app';
import _ from 'lodash';

// Pages
import landing from 'pages/landing/landing';
import printCards from 'pages/printCards/printCards';
import settings from 'pages/settings/settings';
import settingsNav from 'pages/settings/settingsNav/settingsNav';
import settingsOverview from 'pages/settings/settingsOverview/settingsOverview';
import activity from 'pages/activity/activity';
import claim from 'pages/activity/claim/claim';
import submitClaim from 'pages/activity/submitClaim/submitClaim';
import sponsorLegal from 'pages/sponsorLegal/sponsorLegal';
import posing from 'components/header/posingBanner/posingBanner';
import familyAccessEdit from 'pages/settings/familyAccessEdit/familyAccessEdit';
import partnersComponent from 'pages/getCare/partners/partners';
import getCareLandingBannerComponent from 'pages/getCare/getCareLandingBanner/getCareLandingBanner';
import getCareLandingTitleComponent from 'pages/getCare/getCareLandingTitle/getCareLandingTitle';
import zendeskInboxComponent from 'pages/zendeskInbox/zendeskInbox';
import headerComponent from 'components/header/header';
import CARDS_PERIOD from 'constants/cardsPeriod';
import ROUTES from './routes.json';

import { initializeAmplitude } from './helpers/analytics';
import { logEvent, eventCategories, setAmplitudeUser } from 'helpers/analytics';

import { isProd } from 'helpers/checkEnv';

// page components
import 'pages/settings/familyAccessRead/familyAccessRead';

import {
  userAccount,
  userAccountEdit
} from 'pages/settings/userAccount/userAccount';

import {
  commPreferences,
  commPreferencesEdit
} from 'pages/settings/commPreferences/commPreferences';

// import cob from 'pages/settings/cob/cob';
// import cobOverview from 'pages/settings/cobOverview/cobOverview';

import familyInviteRead from 'pages/settings/familyInviteRead/familyInviteRead';
import familyInviteEdit from 'pages/settings/familyInviteEdit/familyInviteEdit';

import mxConfirmationModal from 'components/modals/mxConfirmationModal/mxConfirmationModal';
// import MemberCob from 'models/cob/MemberCob';

app.config(($stateProvider, GetCareProvider) => {
  // Abstract base state that ensures that child states are protected
  // as well as provides named states for header and footer
  $stateProvider.state('Auth', {
    abstract: true,
    parent: 'Base',
    url: '?sponsorshipId&loginEmail',
    params: {
      showVerificationSuccess: false
    },
    resolve: {
      currentUser: User => User.getUser(),
      // unused currentUser to prevent this from calling unless user is logged in.
      // fix the linking issue
      // eslint-disable-next-line no-unused-vars
      allSponsorships: (Sponsor, currentUser) => {
        return Sponsor.getSponsorships();
      },
      sponsorshipId: (allSponsorships, $stateParams) => {
        return (
          parseInt($stateParams.sponsorshipId, 10) ||
          allSponsorships.sponsorships[0].id
        );
      },
      currentSponsorship: (Sponsor, allSponsorships, sponsorshipId) => {
        Sponsor.setCurrentSponsorship(allSponsorships, sponsorshipId);
        return Sponsor.getCurrentSponsorship();
      },
      planMemberships: (PlanMembership, sponsorshipId) => {
        return PlanMembership.getPlanMemberships(sponsorshipId);
      },
      currentSponsor: currentSponsorship => {
        return currentSponsorship.sponsor;
      },
      isSubscriber: (currentUser, currentSponsorship) => {
        return currentUser.user.id === currentSponsorship.subscriber.id;
      },
      shouldShowInbox: (Sponsor, currentSponsor, allSponsorships) => {
        const sponsorIds = allSponsorships.sponsorships.map(
          sponsorship => sponsorship.sponsor.id
        );
        return Sponsor.getSponsorZendeskSettings(sponsorIds);
      },
      navLinks: (planMemberships, NavLinks) => {
        return NavLinks.getNavLinks(planMemberships);
      },
      familyUnit: planMemberships => {
        return planMemberships.coveredMembers;
      },
      showVerificationSuccess: $stateParams =>
        $stateParams.showVerificationSuccess
    },
    onEnter: (
      currentUser,
      $state,
      chModal,
      Idle,
      showVerificationSuccess,
      currentSponsorship,
      Posing,
      Chat
    ) => {
      // If Idle timer is not running when Auth state is entered, start it
      if (!Idle.running()) {
        Idle.watch();
      }

      Chat.initiateChat();

      if (showVerificationSuccess) {
        chModal
          .open({
            component: mxConfirmationModal,
            locals: {
              header: 'Success!',
              body: 'Your email has been changed',
              confirm: 'close'
            }
          })
          .on('close', () => {
            $state.go($state.current, { showVerificationSuccess: false });
          });
      }

      if (Posing.isPosing()) {
        (async () => {
          await initializeAmplitude();
        })();
      }

      setAmplitudeUser(currentUser.user.userIdentifier, {
        activePlanTypes: currentSponsorship.activePlanTypes,
        selectedSponsorName: currentSponsorship.sponsor.name,
        selectedSponsorCode: currentSponsorship.sponsor.sponsorCode
      });
    },
    views: {
      'header@Base': {
        component: headerComponent
      },
      'posing@Base': {
        component: posing
      }
    }
  });

  $stateProvider.state('Landing', {
    url: '',
    parent: 'Auth',
    resolve: {
      /*
      cob(CobService, currentUser, planMemberships) {
        // TODO: future iterations of COB backend API will handle this requirement for us
        // The “Yes”/No question will only appear to members 18 and up
        // with a COB status of undefined who have an active CH administered Medical plan
        if (!planMemberships.hasCurrentOrFutureMedicalPlanMembership) {
          return new MemberCob({
            status: 'NO',
            isPending: false
          });
        }

        // Otherwise, proceed as usual
        return CobService.getCob(currentUser.user);
      }
      */
    },
    onEnter() {
      logEvent('homeViewed', {
        category: eventCategories.HOME
      });
    },
    views: {
      'content@Base': {
        component: landing
      }
    }
  });

  $stateProvider.state('Activity', {
    url: `${ROUTES.activityPage.angular}?patientId&planType`,
    params: {
      patientId: {
        type: 'int',
        value: 0
      }
    },
    parent: 'Auth',
    resolve: {
      selectedUser: $stateParams => $stateParams.patientId,
      sponsorshipPeriod: (Sponsor, currentSponsorship) => {
        return Sponsor.getSponsorshipPeriod(currentSponsorship.id);
      }
    },
    onEnter($transition$, $stateParams) {
      $transition$.onSuccess({}, transition => {
        if (transition.from().name !== 'Activity') {
          if (!$stateParams.planType && $stateParams.patientId === 0) {
            logEvent('activityHomeViewed', {
              category: eventCategories.ACTIVITY
            });
          }
        }

        logEvent('claimsViewed', {
          category: eventCategories.ACTIVITY,
          planType: $stateParams.planType || 'all'
        });
      });
    },
    data: {
      title: 'Collective Health | My Activity'
    },
    views: {
      'content@Base': {
        component: activity
      }
    }
  });

  $stateProvider.state('Claim', {
    url: '/claim/:claimId',
    parent: 'Activity',
    resolve: {
      claim: [
        '$stateParams',
        '$q',
        'Claims',
        ($stateParams, $q, Claims) => {
          return Claims.getClaim($stateParams.claimId).then(claim => {
            if (claim.type === 'pharmacyClaim') {
              return $q.reject(new Error('claimNotFound'));
            }

            return claim;
          });
        }
      ],
      isRegistered: (claim, currentUser, User) => {
        const patientId = claim.patient.id;
        // if patientId is not defined we assume it to be current user who is registered
        if (!patientId || currentUser.user.id === patientId) {
          return true;
        }
        return User.isMemberRegistered(patientId, currentUser.user.id);
      }
    },
    views: {
      'content@Base': {
        component: claim
      }
    },
    data: {
      title: 'Collective Health | Claim Detail'
    }
  });

  $stateProvider.state('SubmitClaim', {
    url: '/submitClaim',
    parent: 'Activity',
    views: {
      'content@Base': {
        component: submitClaim
      }
    },
    resolve: {
      plans: (currentSponsorship, currentUser, SubmitClaimService) =>
        SubmitClaimService.getPlans(
          currentUser.user.id,
          currentSponsorship.sponsor.id
        ),
      planTypes: planMemberships => {
        return planMemberships.planTypesClaimSubmission;
      }
    }
  });

  GetCareProvider.configureStates({
    urlPrefix: 'getCare',
    parent: 'Auth',
    viewTarget: 'content@Base',
    onEnter(
      $state,
      $stateParams,
      currentSponsorship,
      GetCare,
      lat,
      lng,
      LocationService,
      currentUser,
      $transition$
    ) {
      'ngInject';
      if (!lat || !lng) {
        return LocationService.getUsersGeocodedAddress(
          currentUser,
          currentSponsorship.sponsor
        ).then(({ latitude, longitude, locationQuery }) => {
          return $state.target(
            GetCare.landing.stateName,
            {
              lat: latitude,
              lng: longitude,
              locationQuery,
              '#': $stateParams['#']
            },
            {
              reload: 'Auth'
            }
          );
        });
      }

      $transition$.onSuccess({}, transition => {
        if (transition.from().name !== GetCare.landing.stateName) {
          logEvent('getCareHomeViewed', {
            category: eventCategories.CARE
          });
        }
      });
    },
    onExit() {
      'ngInject';

      logEvent('getCareExited', {
        category: eventCategories.CARE
      });
    },
    costFinderPrintEnabledResolve(Posing) {
      'ngInject';

      return Posing.isPosing();
    },
    drFinderPrintEnabledResolve() {
      'ngInject';

      return true;
    },
    planIdsResolve(currentSponsorship) {
      'ngInject';

      return currentSponsorship.activePlans.map(plan => plan.id);
    },
    partnerEnabledResolve() {
      'ngInject';

      return true;
    },
    sponsorshipResolve(currentSponsorship) {
      'ngInject';

      return currentSponsorship;
    },
    userResolve(currentUser) {
      'ngInject';

      return currentUser;
    },
    showCenterOfExcellenceResolve(currentSponsorship) {
      'ngInject';

      return currentSponsorship.activePlans.some(
        plan => plan.hasCentersOfExcellence
      );
    },

    options: {
      mapResults: {
        showYelp: true
      },
      costInformation: {
        enabled: true,
        planPageStateName: 'Plan'
      },
      centerOfExcellence: {
        enabled: true
      },
      landing: {
        hideMap: true,
        landingBannerComponent: getCareLandingBannerComponent,
        landingDetailsComponent: partnersComponent,
        landingTitleComponent: getCareLandingTitleComponent,
        resolve: {
          scrollToPartners: $stateParams => {
            'ngInject';
            return $stateParams['#'] === 'partners';
          }
        }
      }
    }
  });

  $stateProvider.state('Settings', {
    url: 'settings',
    parent: 'Auth',
    abstract: true,
    resolve: {
      familyUnit: planMemberships => {
        // We don't let users under the age of 13 have accounts
        return planMemberships.coveredMembersOver13;
      }
    },
    onEnter() {
      logEvent('settingsViewed', {
        category: eventCategories.SETTINGS
      });
    },
    views: {
      'content@Base': {
        component: settings
      },
      'settingsNav@Settings': {
        component: settingsNav
      }
    }
  });

  $stateProvider.state('SettingsOverview', {
    url: '',
    parent: 'Settings',
    params: {
      showVerificationError: false
    },
    data: {
      isEdit: false
    },
    resolve: {
      showVerificationError: $stateParams => $stateParams.showVerificationError
    },
    views: {
      'overview@Settings': {
        component: settingsOverview
      },
      'userAccount@SettingsOverview': {
        component: userAccount
      },
      'commPreferences@SettingsOverview': {
        component: commPreferences
      },
      'familyInvite@SettingsOverview': {
        component: familyInviteRead
      }
      // 'cob@SettingsOverview': {
      //   component: cobOverview
      // }
    }
  });

  // TODO setup redirects for /feature requests
  $stateProvider.state('SettingsAccount', {
    url: '/account/edit',
    parent: 'Settings',
    data: {
      isEdit: true
    },
    views: {
      'edit@Settings': {
        component: userAccountEdit
      }
    }
  });

  $stateProvider.state('SettingsCommunication', {
    url: '/communication/edit',
    parent: 'Settings',
    data: {
      isEdit: true
    },
    views: {
      'edit@Settings': {
        component: commPreferencesEdit
      }
    }
  });

  // $stateProvider.state('SettingsCob', {
  //   url: '/cob/overview',
  //   parent: 'Settings',
  //   data: {
  //     isEdit: true
  //   },
  //   onEnter($state, cob) {
  //     if (!cob.showToMember) {
  //       return $state.target('SettingsOverview');
  //     }
  //   },
  //   views: {
  //     'edit@Settings': {
  //       component: cob
  //     }
  //   }
  // });

  $stateProvider.state('SettingsFamilyAccess', {
    url: '/access/edit/{granteeId:int}',
    parent: 'Settings',
    data: {
      isEdit: true
    },
    views: {
      'edit@Settings': {
        component: familyAccessEdit
      }
    }
  });

  $stateProvider.state('SettingsFamilyAuthorize', {
    url: '/authorization/edit/{dependentId:int}',
    parent: 'Settings',
    data: {
      isEdit: true
    },
    resolve: {
      isInvited: ($stateParams, currentUser, User) => {
        return User.getFamilyUnitRegStatus(currentUser.user.id).then(
          registrationStatuses => {
            return _.get(
              _.find(registrationStatuses, {
                personId: $stateParams.dependentId
              }),
              'invited',
              false
            );
          }
        );
      },
      dependentId: $stateParams => $stateParams.dependentId,
      isRegistered: (dependentId, currentUser, User) => {
        return User.isMemberRegistered(dependentId, currentUser.user.id);
      }
    },
    views: {
      'edit@Settings': {
        component: familyInviteEdit
      }
    }
  });

  $stateProvider.state('SponsorLegal', {
    url: 'legal',
    parent: 'Auth',
    resolve: {
      activePlans(PlanDetails, currentSponsorship) {
        return PlanDetails.getPlanDetails(currentSponsorship.id);
      },
      legalDocuments(User, currentSponsorship) {
        return User.getUserLegalInfo(currentSponsorship.id);
      }
    },
    views: {
      'content@Base': {
        component: sponsorLegal
      }
    }
  });

  $stateProvider.state('PrintCards', {
    url: 'printcards?selectedUserId&cardPeriod',
    parent: 'Auth',
    resolve: {
      selectedUserId: $stateParams => parseInt($stateParams.selectedUserId, 10),
      selectedUser: (planMemberships, selectedUserId) =>
        planMemberships.getCoveredMember(selectedUserId),
      cardPeriod: $stateParams => $stateParams.cardPeriod,
      cardsForPeriod(Cards, selectedUserId, currentSponsorship, cardPeriod) {
        return Cards.getCardsByMember(
          currentSponsorship.id,
          selectedUserId
        ).then(memberCards => {
          if (memberCards.hasCurrentAndFuture) {
            return memberCards[cardPeriod];
          }

          if (memberCards.hasFuture) {
            return memberCards[CARDS_PERIOD.FUTURE];
          }

          return memberCards[CARDS_PERIOD.CURRENT];
        });
      }
    },
    views: {
      '@': {
        component: printCards
      }
    }
  });

  $stateProvider.state('FundsSso', {
    url: 'fundsSso?spendingAccountId&partnerId',
    parent: 'Auth',
    resolve: {
      spendingAccountId: $stateParams => $stateParams.spendingAccountId,
      partnerId: $stateParams => $stateParams.partnerId
    },
    onEnter(
      currentSponsorship,
      partnerId,
      spendingAccountId,
      SpendingAccountsService
    ) {
      SpendingAccountsService.ssoIntoProductPartner(
        currentSponsorship.sponsor.id,
        spendingAccountId,
        partnerId
      );
    },
    views: {
      '@': {
        template: '' // We show a blank screen because once we redirect it goes to a blank screen anyways
      }
    }
  });

  $stateProvider.state('Inbox', {
    url: 'inbox?ticketId&messageUuid&categoryCompose',
    parent: 'Auth',
    resolve: {
      userIdentifier(User) {
        return User.getUser().then(data => {
          return data.user.userIdentifier || '';
        });
      },
      ticketId($stateParams) {
        return $stateParams.ticketId || null;
      },
      categoryCompose($stateParams, userIdentifier) {
        return (
          !!$stateParams.categoryCompose &&
          $stateParams.messageUuid === userIdentifier
        );
      },
      isRecommendationsEnabled: Recommendations => {
        return Recommendations.isRecommendationsEnabled();
      },
      goToCompose() {
        return false;
      }
    },
    onEnter($state, shouldShowInbox) {
      if (!shouldShowInbox) {
        $state.go('Landing');
      }
    },
    views: {
      'content@Base': {
        component: zendeskInboxComponent
      }
    }
  });

  $stateProvider.state('InboxCompose', {
    url: 'inbox/compose',
    parent: 'Auth',
    resolve: {
      goToCompose() {
        return true;
      },
      isRecommendationsEnabled: Recommendations => {
        return Recommendations.isRecommendationsEnabled();
      }
    },
    onEnter($state, shouldShowInbox) {
      if (!shouldShowInbox) {
        $state.go('Landing');
      }
    },
    views: {
      'content@Base': {
        component: zendeskInboxComponent
      }
    }
  });

  $stateProvider.state('RecommendationsInbox', {
    url: 'suggestions?source',
    parent: 'Auth',
    resolve: {
      userIdentifier(User) {
        return User.getUser().then(data => {
          return data.user.userIdentifier || '';
        });
      },
      isRecommendationsEnabled: Recommendations => {
        return Recommendations.isRecommendationsEnabled();
      },
      recommendationId() {
        return null;
      },
      recommendationsInbox($stateParams) {
        return true;
      },
      source($stateParams) {
        return $stateParams.source || null;
      }
    },
    views: {
      'content@Base': {
        component: zendeskInboxComponent
      }
    }
  });

  $stateProvider.state('RecommendationsSingle', {
    url: 'suggestions/:recommendationId?source',
    parent: 'Auth',
    resolve: {
      userIdentifier(User) {
        return User.getUser().then(data => {
          return data.user.userIdentifier || '';
        });
      },
      isRecommendationsEnabled: Recommendations => {
        return Recommendations.isRecommendationsEnabled();
      },
      recommendationId($stateParams) {
        return $stateParams.recommendationId || null;
      },
      recommendationsInbox($stateParams) {
        return true;
      },
      source($stateParams) {
        return $stateParams.source || null;
      }
    },
    onEnter($state, isRecommendationsEnabled) {
      if (!isRecommendationsEnabled) {
        $state.go('404', {}, { location: false });
      }
    },
    views: {
      'content@Base': {
        component: zendeskInboxComponent
      }
    }
  });

  // email link
  $stateProvider.state('Messages', {
    url: 'messages',
    parent: 'Auth',
    onEnter($state) {
      $state.go('Inbox');
    }
  });

  //inbox alias with email ticket linking
  $stateProvider.state('MessagesCompose', {
    url: 'message/compose/:ticketId/:uuid',
    parent: 'Auth',
    onEnter($state, $stateParams) {
      $state.go('Inbox', {
        ticketId: $stateParams.ticketId,
        messageUuid: $stateParams.uuid,
        categoryCompose: true
      });
    }
  });
});
